import React, { useRef, useState } from "react";
import { motion } from "framer-motion";
import { Volume2, VolumeX, Play, Pause } from "lucide-react";
import { ASSETS } from "../lib/api";
import { useT } from "../i18n/LanguageContext";

export const VideoManifesto = () => {
  const { t } = useT();
  const x = t.manifesto;
  const videoRef = useRef(null);
  const [muted, setMuted] = useState(true);
  const [playing, setPlaying] = useState(true);

  const toggleMute = () => {
    if (!videoRef.current) return;
    videoRef.current.muted = !videoRef.current.muted;
    setMuted(videoRef.current.muted);
  };
  const togglePlay = () => {
    if (!videoRef.current) return;
    if (videoRef.current.paused) { videoRef.current.play(); setPlaying(true); }
    else { videoRef.current.pause(); setPlaying(false); }
  };

  return (
    <section className="relative section-pad-tight section-pad-bottom-tight overflow-hidden" data-testid="manifesto-section">
      <div className="absolute inset-0 grain pointer-events-none" />
      <div
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[90vw] h-[60vw] max-w-[1100px] max-h-[700px] rounded-full pointer-events-none"
        style={{ background: "radial-gradient(ellipse, rgba(212,97,42,0.10) 0%, transparent 65%)" }}
      />

      <div className="relative max-w-[1200px] mx-auto">
        <div className="text-center mb-12 md:mb-16">
          <motion.span
            initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}
            transition={{ duration: 0.8 }} className="eyebrow"
          >
            {x.eyebrow}
          </motion.span>
          <motion.h2
            initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            transition={{ duration: 1, ease: [0.22, 0.61, 0.36, 1] }}
            className="mt-5 font-display font-light text-[32px] sm:text-[44px] md:text-[60px] leading-[1.05] tracking-[-0.012em] text-[var(--pn-milk)]"
          >
            {x.title_a}<em className="italic text-[var(--pn-amber)]">{x.title_b}</em>{x.title_c}
          </motion.h2>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 1.03 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 1.3, ease: [0.22, 0.61, 0.36, 1] }}
          className="relative aspect-[9/16] sm:aspect-[4/5] md:aspect-[16/9] max-w-[920px] mx-auto bg-black rounded-sm overflow-hidden shadow-[0_30px_80px_-20px_rgba(0,0,0,0.7)] ring-1 ring-[rgba(201,160,88,0.12)]"
        >
          {/* Blurred background: same video, scaled & blurred to fill the letterbox bands elegantly */}
          <video
            src={ASSETS.heroVideo}
            autoPlay muted loop playsInline preload="metadata"
            aria-hidden
            className="absolute inset-0 w-full h-full object-cover scale-110 opacity-60 pointer-events-none"
            style={{ filter: "blur(28px) saturate(1.15) brightness(0.7)" }}
          />
          {/* Subtle warm overlay over the blur */}
          <div className="absolute inset-0 bg-gradient-to-br from-[#0E0A07]/55 via-transparent to-[#0E0A07]/55 pointer-events-none" />

          {/* Main video — contained on desktop (so vertical 9:16 fits entirely without zoom),
              covering on mobile where the frame is also vertical */}
          <video
            ref={videoRef} src={ASSETS.heroVideo} poster={ASSETS.attimosphere1}
            autoPlay muted loop playsInline preload="metadata"
            className="relative w-full h-full object-cover md:object-contain z-[1]"
            data-testid="manifesto-video"
          />
          <div className="absolute bottom-4 right-4 flex items-center gap-2 z-10">
            <button onClick={togglePlay}
              className="w-11 h-11 rounded-full flex items-center justify-center text-[var(--pn-milk)] backdrop-blur-md bg-black/40 border border-white/15 hover:bg-black/55 hover:border-[var(--pn-amber)]/60 transition-all duration-300"
              data-testid="manifesto-toggle-play"
            >
              {playing ? <Pause size={16} /> : <Play size={16} className="ml-0.5" />}
            </button>
            <button onClick={toggleMute}
              className="w-11 h-11 rounded-full flex items-center justify-center text-[var(--pn-milk)] backdrop-blur-md bg-black/40 border border-white/15 hover:bg-black/55 hover:border-[var(--pn-amber)]/60 transition-all duration-300"
              data-testid="manifesto-toggle-mute"
            >
              {muted ? <VolumeX size={16} /> : <Volume2 size={16} />}
            </button>
          </div>
          {muted && (
            <div className="absolute bottom-4 left-4 z-10 hidden md:block">
              <button onClick={toggleMute}
                className="px-3.5 py-2 rounded-full backdrop-blur-md bg-black/40 border border-[var(--pn-amber)]/40 text-[var(--pn-amber)] text-[12px] tracking-wider uppercase font-medium hover:bg-black/55 transition-all duration-300"
              >
                {x.audioOn}
              </button>
            </div>
          )}
        </motion.div>

        <motion.p
          initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}
          transition={{ delay: 0.5, duration: 0.9 }}
          className="mt-10 md:mt-12 text-center font-display italic text-[15px] md:text-[17px] text-[var(--pn-cream)]/60"
        >
          {x.caption}
        </motion.p>
      </div>
    </section>
  );
};
